import * as Logger from "js-logger";
import {Signal} from "signals";
import {StopAutoplayCommand} from "slotworks/commands/autoplay/stop-autoplay";
import Components from "slotworks/components/components";
import GameLoop from "slotworks/core/game-loop";
import {GameiomWrapper} from "slotworks/integration/gameiom/GameiomWrapper";
import {PariplayWrapper} from "slotworks/integration/pariplay/PariplayWrapper";
import RevolverModel from "slotworks/integration/revolver/model/revolver-model";
import {InitRecordSchema} from "slotworks/integration/revolver/schema/init-record-schema";
import {SpinResponseSchema} from "slotworks/integration/revolver/schema/spin-record-schema";
import SmartButton, {SmartButtonFlag} from "slotworks/smart-button/smart-button";
import Contract from "slotworks/utils/contract";
import AbstractMediator from "../../../mediators/abstract-mediator";
import {DefaultWrapper} from "./DefaultWrapper";
import {WrapperEvent} from "./WrapperEvent";
import { RelaxSEWrapper } from "slotworks/integration/relax/RelaxSEWrapper";
import StateMachine from "slotworks/state-machine/state-machine";
import { PlaytechWrapper } from "slotworks/integration/playtech/PlaytechWrapper";
import { EveryMatrixWrapper } from "slotworks/integration/emtrx/EmtrxWrapper";

export interface IWrapperMediator {
    init: () => void;
    quit: () => void;
    gameDataLoaded: (response: InitRecordSchema) => void;
    gameLoaded: () => void;
    roundStart: (totalBet: number, balance: number) => void;
    roundStarted: (spinId: number, balanceBeforeSpin?: number) => void;
    spinResponseReceived: (response: SpinResponseSchema) => void;
    balanceUpdated: (balance: number) => void;
    reportPaused: () => void;
    roundEnd: (win: number, bet: number, balance: number) => void;
    autoplayStart: () => void;
    autoplayEnd: () => void;
}

export interface IIntegrationWrapper extends IWrapperMediator {
    events: Signal;
    dispose: () => void;
}

const wrapperByBrandingPrefix = new Map<string, {new(): IIntegrationWrapper}>()
    .set("prp", PariplayWrapper)
    .set("gameiom", GameiomWrapper)
    .set('rlx', RelaxSEWrapper)
    .set('relax', RelaxSEWrapper)
    .set('plt', PlaytechWrapper)
    .set('emtrx', EveryMatrixWrapper);

class WrapperMediator extends AbstractMediator implements IWrapperMediator {
    
    public onWrapperPauseChange: Signal;

    private wrapper: IIntegrationWrapper;
    private balanceBeforeSpin: number;
    private pauseState = {
        pointerEvents: "all",
        hasPromptFlag: false,
        isBetBarEnabled: true
    };

    public constructor() {
        super();
        this.start = this.start.bind(this);
        this.end = this.end.bind(this);

        this.onWrapperPauseChange = new Signal();
    }

    public start() {
        return new Contract((resolve) => {
            this.log("Start");

            const WrapperClass = wrapperByBrandingPrefix.get(this.getBranding()) || DefaultWrapper;
            this.log("Detected", WrapperClass);

            this.wrapper = new WrapperClass();
            this.setupListeners();
            resolve(null);
        });
    }

    public end() {
        this.log("End");
        this.removeListeners();
        this.wrapper.dispose();
        this.wrapper = null;
        this.pauseState = null;
    }

    // IWrapperMediator implementation
    public init = () => {
        this.log("init");
        this.wrapper.init();
    }

    public quit = () => {
        this.log("quit");
        this.wrapper.quit();
    }

    public getBranding = () => (RevolverModel.getBranding() || "").split("-")[0]

    public gameDataLoaded = (response: InitRecordSchema) => {
        this.log("gameDataLoaded", response);
        this.wrapper.gameDataLoaded(response);
    }

    public gameLoaded = () => {
        this.log("gameLoaded");
        this.wrapper.gameLoaded();
    }

    public roundStart = (totalBet: number, balance: number) => {
        this.log(`roundStart - totalBet: ${totalBet} balance: ${balance}`);
        this.balanceBeforeSpin = balance;
        this.wrapper.roundStart(totalBet, balance);
    }

    public roundStarted = (spinId: number) => {
        this.log("roundStarted - spinId:", spinId, "balanceBefore:", this.balanceBeforeSpin);
        this.wrapper.roundStarted(spinId, this.balanceBeforeSpin);
    }

    public spinResponseReceived = (response: SpinResponseSchema) => {
        this.log("spinResponseReceived", response);
        this.wrapper.spinResponseReceived(response);
    }

    public balanceUpdated = (balance: number) => {
        this.log("balanceUpdated", balance);
        this.wrapper.balanceUpdated(balance);
    }

    public reportPaused = () => {
        this.wrapper.reportPaused();
    }

    public roundEnd = (win: number, bet: number, balance: number) => {
        this.log("roundEnd", win, bet, balance);
        this.wrapper.roundEnd(win, bet, balance);
    }

    public autoplayStart = () => {
        this.log("autoplayStart");
        this.wrapper.autoplayStart();
    };
    public autoplayEnd = () => {
        this.log("autoplayEnd");
        this.wrapper.autoplayEnd();
    };

    private setupListeners = () => {
        this.wrapper.events.add(this.onWrapperEvent);
    }

    private removeListeners = () => {
        this.wrapper.events.remove(this.onWrapperEvent);
    }

    private log = (...args: any[]) => {
        Logger.info("%c Wrapper Mediator ", "background: #4d64e3; color: #fff", ...args);
    }

    // Event handling
    private onWrapperEvent = (type: WrapperEvent, data?: any) => {
        // @ts-ignore
        const handler = this[`onWrapper_${type}`] as (data?: any) => void;
        if (handler) {
            handler(data);
        }
    }

    private onWrapper_Pause = (data?: any) => {
        if (GameLoop.isPaused()) {
            return;
        }

        let pause = (wait: boolean = false) => {
            const pointerEvents = document.body.style.pointerEvents;
            document.body.style.pointerEvents = "none";
    
            const hasPromptFlag = SmartButton.hasFlag(SmartButtonFlag.PROMPT);
            SmartButton.setFlag(SmartButtonFlag.PROMPT, true);
    
            let isBetBarEnabled = Components.betBar.isEnabled();
            if(wait) {
                isBetBarEnabled = true;
            }
            this.pauseState = {
                hasPromptFlag,
                isBetBarEnabled,
                pointerEvents,
            };
    
            GameLoop.setPaused(true);
            Components.betBar.setEnabled(false);

            this.reportPaused();
            this.onWrapperPauseChange.dispatch(true);
        };

        let waitForSpinFinish = data && data.waitForSpinFinish;

        let currentState = StateMachine.currentState.node.name;
        if(waitForSpinFinish && (currentState && !currentState.includes("loop-wins") && !currentState.includes("idle") && !currentState.includes("autoplay"))) {
            let onSpinFinished = (name: string) => {
                if(name && (name.includes("loop-wins") || name.includes("idle") || name == "autoplay")) {
                    pause(true);
                    StateMachine.onStateEnter.remove(onSpinFinished);
                }
            }
            StateMachine.onStateEnter.add(onSpinFinished);
        }
        else {
            pause();
        }
        
    }

    private onWrapper_Resume = (data?: any) => {
        if (!GameLoop.isPaused()) {
            return;
        }

        const {pointerEvents, hasPromptFlag, isBetBarEnabled} = this.pauseState;

        document.body.style.pointerEvents = pointerEvents;

        GameLoop.setPaused(false);
        Components.betBar.setEnabled(isBetBarEnabled);
        SmartButton.setFlag(SmartButtonFlag.PROMPT, hasPromptFlag);

        this.onWrapperPauseChange.dispatch(false);
    }

    private onWrapper_StopAutobet = (data?: any) => {
        StopAutoplayCommand();
    }

    private onWrapper_EnableSpin = (data?: any) => {
        SmartButton.setFlag(SmartButtonFlag.PROMPT, false);
    }

    private onWrapper_DisableSpin = (data?: any) => {
        SmartButton.setFlag(SmartButtonFlag.PROMPT, true);
    }
}

const wrapperMediator = new WrapperMediator();
export default wrapperMediator;



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/integration/revolver/wrapper/wrapper-mediator.ts